<template>
  <div class="bbe-template">
    <transition name="bbe-fade" appear>
      <loader-fly v-if="loaderRun" :fill="true"/>
    </transition>
    <div class="bbe-template-header">
      <div class="bbe-template-header-details">
        <label class="bbe-label">
          <input class="bbe-template-header-details__item bbe-input"
                 :class="{
                   'bbe-template-header-details__item_error': errors.name,
                   'bbe-template-header-details__item_view-mode': viewMode
                 }"
                 ref="bbeInputNameTemplate"
                 type="text"
                 :disabled="viewMode"
                 placeholder="Name of template"
                 v-model="template.name"
                 v-if="!viewMode">
          <span class="bbe-template-header-details__item bbe-input bbe-template-header-details__item_view-mode" v-else>
            <span v-if="template.highlightName" v-html="template.highlightName"></span>
            <span v-else>{{template.name}}</span>
          </span>
          <transition name="bbe-bounce">
            <span class="bbe-label__error" v-if="errors.name">{{errors.name}}</span>
          </transition>
        </label>
      </div>
      <transition name="bbe-routing-fade">
      <div class="bbe-template-header__toolbar">
        <ToolbarTextEditor :quill="quill"
                           v-if="!viewMode"
                           :formatting="activeStyle"
                           :selectDown="true"
                           @uploadFile="getFiles"/>
      </div>
      </transition>
    </div>
    <div class="bbe-template-body__wrapper" ref="bbeDropWrapper">
      <transition name="bbe-bounce">
        <span class="bbe-label__error" v-if="errors.content">{{errors.content}}</span>
        <span class="bbe-label__error" v-if="errors.uploadFile">{{errors.uploadFile}}</span>
      </transition>
      <vue-perfect-scrollbar class="bbe-template-body"
                             @dragenter="setCursorFromDrag"
                             @dragover="setCursorFromDrag"
                             @click="setFocusToEditor">
        <div class="bbe-template-body__message"
             id="editorFull"
             ref="bbeEditorFull"/>
      </vue-perfect-scrollbar>
      <DropFiles :visibility="visibilityDrop"/>
    </div>
    <div class="bbe-template-footer">
      <UploadFiles :files="attachFiles"
                   :dropped="dropAttachFiles"
                   :spaces="true"
                   :disabled="viewMode"
                   @startLoad="loadingFile += 1"
                   @endLoad="loadingFile -= 1"/>
      <transition name="bbe-fade" appear>
        <button class="bbe-button bbe-template-footer-send"
                v-if="checkAccess()"
                @click.stop="openSaveTemplate(true)">
          Move
        </button>
      </transition>
      <button class="bbe-button bbe-template-footer-send"
              @click.stop="viewMode ? openEditTemplate() : sendTemplate()"
              v-if="checkAccess()">
        <transition name="bbe-fade" mode="in-out">
            <span class="bbe-template-footer-send__text" v-if="!viewMode">
              Save
            </span>
        </transition>
        <transition name="bbe-fade" mode="in-out">
            <span class="bbe-template-footer-send__text" v-if="viewMode">
              Edit
            </span>
        </transition>
      </button>
    </div>
    <ModalWindow v-if="openModalSaveTemplate"
                 :title="titleSaveTemplate"
                 :right-button="{
                   action: sendTemplate,
                   text: 'Save'
                 }"
                 @close="openModalSaveTemplate = false">
      <TemplateSaveFromMail @getName="setTemplateName"
                            @getFolder="setFolder"
                            :name="template.name"
                            :saveError="templateSaveError"/>
    </ModalWindow>
  </div>
</template>

<script>
import ToolbarTextEditor from '@/components/Main/ToolbarTextEditor/ToolbarTextEditor'
import LoaderFly from '@/components/Main/Loaders/LoaderFly'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import {get, findIndex, some, find} from 'lodash'
import {Jodit} from 'jodit'
import joditConfig from '@/constants/joditConfig'
import templatesApi from '@/api/Templates/api'
import {FOUNDER, ROLES_LIST} from '@/constants/roles'
import getFormatting from '@/mixin/getFormatting'
import UploadFiles from '@/components/Main/UploadFiles/UploadFiles'
import initDropEvents from '@/mixin/initDropEvents'
import DropFiles from '@/components/Main/DropFiles/DropFiles'
import UploadApi from '@/api/Upload/api'
import NotificationApi from '@/api/Notification/api'
import getBlobByUrl from '@/mixin/getBlobByUrl'
import EmailAttachments from '@/components/Email/EmailAttachments/EmailAttachments'
import routerPush from '@/mixin/routerPush'
import TemplateSaveFromMail from '@/components/Templates/TemplateSaveFromMail/TemplateSaveFromMail'
import ModalWindow from '@/components/Main/ModalWindow/ModalWindow'
import getErrorMessage from '@/mixin/getErrorMessage'
import templateTypes from '@/constants/templateTypes'

export default {
  name: 'TemplateOpen',
  components: {
    VuePerfectScrollbar,
    ToolbarTextEditor,
    LoaderFly,
    UploadFiles,
    DropFiles,
    EmailAttachments,
    TemplateSaveFromMail,
    ModalWindow
  },
  mixins: [getFormatting, initDropEvents, getBlobByUrl, routerPush, getErrorMessage],
  data () {
    return {
      titleSaveTemplate: 'Save as template',
      openModalSaveTemplate: false,
      loadingFile: 0,
      visibilityDrop: false,
      attachFiles: [],
      dropAttachFiles: [],
      filesIds: [],
      activeStyle: {},
      editMode: false,
      viewMode: false,
      errors: {
        name: '',
        content: '',
        uploadFile: ''
      },
      quill: '',
      fontSize: 16,
      loaderRun: false,
      template: {
        name: '',
        content: '',
        roles: ROLES_LIST.map(item => item.id)
      }
    }
  },
  computed: {
    templateSaveError () {
      return find(this.errors, item => item)
    },
    activeUser () {
      return this.$store.getters.userInfo || this.$store.getters.activeUser
    },
    isFounder () {
      return this.activeUser.role === FOUNDER.id
    },
    isPersonalTemplate () {
      return this.currentTemplate.templateType === templateTypes.EMPLOYEE_TEMPLATE
    },
    activeTab () {
      return this.$store.getters.activeTab
    },
    currentFolder: {
      get: function () {
        return this.$store.getters.currentFolder
      },
      set: function (data) {
        this.$store.dispatch('setCurrentFolder', data)
      }
    },
    currentTemplate: {
      get: function () {
        return this.$store.getters.currentTemplate
      },
      set: function (data) {
        this.$store.dispatch('setCurrentTemplate', data)
      }
    },
    templatesList: {
      get: function () {
        return this.$store.getters.templatesList
      },
      set: function (data) {
        this.$store.dispatch('setTemplatesList', data)
      }
    },
    newMessage: {
      get: function () {
        return this.$store.getters.newMessage
      },
      set: function (data) {
        this.$store.dispatch('setNewMessage', data)
      }
    }
  },
  methods: {
    setDefaultTemplate () {
      this.template = {
        name: '',
        content: '',
        roles: ROLES_LIST.map(item => item.id)
      }
    },
    openSaveTemplate (saveAs) {
      if (this.viewMode && !saveAs) {
        this.viewMode = false
        return
      }
      this.openModalSaveTemplate = true
      this.errors = {
        name: '',
        content: '',
        uploadFile: ''
      }
    },
    setTemplateName (newName) {
      this.template.name = newName
    },
    setFolder (folder) {
      this.template.newParentFolder = folder.id ? get(folder, 'id', '') : 'main'
    },
    getFiles (filesIds) {
      this.filesIds = filesIds
    },
    checkedUploadFiles () {
      this.filesIds.forEach((id, index) => {
        const img = document.getElementById(id)
        if (!img) this.deleteFile(id, index)
      })
    },
    deleteFile (id, index) {
      UploadApi.deleteFile(id)
        .then(resp => {
          this.filesIds.splice(index, 1)
        })
        .catch((err) => {
          console.error(err)
        })
    },
    setUploadFile () {
      const files = this.attachFiles.map(file => file.id)
      if (files.length > 0) this.template.uploadFile = files
    },
    updateParent (template) {
      if (template.newParentFolder === 'main') template.newParentFolder = ''
      const indexTemplate = findIndex(this.templatesList, {id: template.id})
      templatesApi.updateParent(template)
        .then(() => {
          if (indexTemplate !== -1) {
            this.templatesList.splice(indexTemplate, 1)
          }
        })
        .catch(err => {
          this.errorMessage = this.getErrorMessage(err)
          console.error(err)
        })
    },
    createTemplate (newParentFolder) {
      this.setUploadFile()
      this.template.parentFolder = get(this.currentFolder, 'id', '')
      this.template.dateCreateFolder = new Date()
      this.newMessage.contentTemplate = false
      templatesApi.createTemplate(this.template)
        .then(resp => {
          this.openModalSaveTemplate = false
          this.viewMode = true
          resp.data.uploadFile = this.attachFiles
          this.templatesList.push(resp.data)
          const tabName = this.activeTab.toLowerCase()
          this.currentTemplate = {...resp.data, viewMode: this.viewMode}
          this.routerPush({name: 'TemplateOpen', params: {id: resp.data.id, tab: tabName}})
          if (newParentFolder) {
            resp.data.newParentFolder = newParentFolder
            this.updateParent(resp.data)
          }
        })
        .catch(err => {
          this.errors.name = this.getErrorMessage(err)
          console.error(err)
        })
    },
    updateTemplate (newParentFolder) {
      this.setUploadFile()
      this.template.id = this.currentTemplate.id
      this.newMessage.contentTemplate = false
      templatesApi.updateTemplate(this.template)
        .then(resp => {
          this.openModalSaveTemplate = false
          this.loaderRun = false
          this.viewMode = true
          const index = findIndex(this.templatesList, {id: resp.data.id})
          if (index !== -1) {
            this.templatesList.splice(index, 1, resp.data)
          }
          if (newParentFolder) {
            resp.data.newParentFolder = newParentFolder
            this.updateParent(resp.data)
          }
        })
        .catch(err => {
          this.loaderRun = false
          this.errors.name = this.getErrorMessage(err)
          console.error(err)
        })
    },
    validateInputs () {
      if (!this.template.name) {
        this.errors.name = 'Template name can\'t be blank'
      } else {
        this.errors.name = ''
      }
      if (!this.quill.value) {
        this.errors.content = 'template cannot be empty'
      } else {
        this.errors.content = ''
      }
      if (this.loadingFile) {
        this.errors.uploadFile = 'waiting for a file to load'
      } else {
        this.errors.uploadFile = ''
      }
      return some(this.errors, item => item)
    },
    checkAccess () {
      if (!this.currentTemplate.id) return true
      const owner = this.currentTemplate.userCreateTemplate === this.activeUser.id
      return this.isFounder || owner
    },
    openEditTemplate () {
      this.viewMode = false
    },
    sendTemplate () {
      if (this.validateInputs()) return
      this.template.content = this.quill.value
      this.template.parentFolder = this.currentTemplate.parentFolder
      const newParentFolder = get(this.template, 'newParentFolder', '')
      if (this.isFounder && this.isPersonalTemplate) {
        this.template.templateType = templateTypes.GLOBAL_TEMPLATE
        this.template.userCreateTemplate = this.activeUser.id
      }
      const id = this.editMode || (this.currentTemplate.id && this.currentTemplate.content)
      id ? this.updateTemplate(newParentFolder) : this.createTemplate(newParentFolder)
    },
    setFocusToEditor () {
      this.quill.selection.focus()
    },
    setLoader (data) {
      this.loaderRun = data
    },
    setFontSize (size) {
      this.fontSize = size
    },
    getFontSize (e) {
      const html = get(e, 'toElement.outerHTML', '16')
      const styleFont = html.match(/font-size: \d+px/gmi) || ['16']
      const size = styleFont[0].match(/\d+/gmi) || ['16']
      this.fontSize = size[0] || 16
    },
    setBase64 (files = []) {
      this.attachFiles = []
      files.forEach(file => {
        if (file.base64) {
          this.attachFiles.push(file)
          return
        }
        if (!file.path) return
        this.getBlobByUrl(file.path)
          .then(resp => {
            const reader = new FileReader()
            reader.readAsDataURL(resp.data)
            reader.onload = () => {
              file.name = file.label || file.name
              file.type = resp.data.type
              file.size = resp.data.size
              file.status = true
              file.uploaded = true
              file.base64 = reader.result
              this.attachFiles.push(file)
            }
          })
          .catch((err) => {
            console.error(err)
          })
      })
    },
    setTemplate () {
      this.errors.name = ''
      this.errors.content = ''
      this.viewMode = this.currentTemplate.viewMode || false
      this.template.name = this.currentTemplate.name || ''
      this.template.highlightName = Array.isArray(this.currentTemplate.highlightName)
        ? this.currentTemplate.highlightName[0]
        : ''
      this.setBase64(this.currentTemplate.uploadFile)
      this.quill.value = ''
      this.editMode = this.$route.params.id && this.$route.params.id !== 'new'
      const highlightContent = Array.isArray(this.currentTemplate.highlightContent)
        ? this.currentTemplate.highlightContent[0]
        : this.currentTemplate.content
      const checkedContent = this.viewMode ? highlightContent : this.currentTemplate.content
      const content = checkedContent || ''
      this.quill.selection.insertHTML(content)
      this.quill.editor.setAttribute('contenteditable', !this.viewMode)
      if (this.newMessage.contentTemplate) {
        this.quill.value = ''
        this.quill.selection.insertHTML(this.newMessage.contentTemplate)
      }
    },
    getStyles (e) {
      if (e) {
        this.activeStyle = this.getFormatting(e.path)
      }
    },
    setCursorFromDrag (e) {
      this.quill.selection.insertCursorAtPoint(e.pageX, e.pageY)
    },
    handleDrop (e) {
      const dt = e.dataTransfer
      const files = dt.files
      this.dropAttachFiles = [...files]
    },
    initDrop () {
      const target = this.$refs.bbeDropWrapper
      this.initDropEvents({
        target: target,
        drop: this.handleDrop,
        dragEnter: () => { this.visibilityDrop = true },
        dragLeave: () => { this.visibilityDrop = false }
      })
    },
    setFocusToName () {
      const input = this.$refs.bbeInputNameTemplate
      if (this.$route.params.id === 'new' && input) {
        this.$nextTick(() => input.focus())
      }
    },
    deleteClassHighlight () {
      this.quill.value = this.quill.value.replace(/bbe-highlight-search/gmi, '')
    },
    async checkNotification () {
      const userId = get(this.activeUser, 'id', '')
      const notify = get(this.currentTemplate, 'notify', [])
      if (!userId || notify.length === 0) return
      const promisesNotify = notify.map(async item => {
        item.readUsers.push(userId)
        item = await this.updateNotification(item)
        return item
      })
      await Promise.all(promisesNotify)
      const template = find(this.templatesList, {id: this.currentTemplate.id})
      template.notify = []
    },
    async updateNotification (notification) {
      try {
        const resp = await NotificationApi.updateNotification(notification)
        return resp
      } catch (e) {
        console.error(e)
      }
    }
  },
  watch: {
    currentTemplate () {
      this.setDefaultTemplate()
      this.setTemplate()
      this.checkNotification()
    },
    $route () {
      this.setFocusToName()
    },
    viewMode () {
      this.deleteClassHighlight()
      this.quill.editor.setAttribute('contenteditable', !this.viewMode)
      this.setFocusToEditor()
    }
  },
  mounted () {
    this.initDrop()
    joditConfig.editorCssClass = 'bbe-template-body__message'
    joditConfig.editorCssClass = 'bbetexteditor'
    this.$route.params.id === 'new' ? joditConfig.autofocus = false : joditConfig.autofocus = true
    this.quill = new Jodit('#editorFull', joditConfig)
    this.quill.events.on('click', (e) => {
      this.getStyles(e)
    })
    this.setFocusToName()
    this.setTemplate()
    this.checkNotification()
  }
}
</script>

<style lang="scss" scoped>
  @import "../../../sass/variables";
  @import "../../../sass/mixins";
  .bbe-template {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;

    &-header {
      position: relative;
      margin-bottom: 11px;

      &-details {
        padding: 0 30px;
        transition: .3s;
        margin-top: 12px;

        &__item {
          border-radius: 0;
          padding: 8px 0;
          margin-top: 6px;
          border-bottom: 1px solid $color-gallery;

          &_error {
            border-bottom: 1px solid $color-pomegranate ;
          }

          &_view-mode {
            color: $color-black;
            font-weight: 600;
            font-size: 22px;
            line-height: 26px;
          }
        }
      }

      &__toolbar {
        margin: 0 22px 0 0;
      }
    }

    &__threads {
      height: 100%;
      max-height: 100%;
      padding: 0 30px;
    }

    &-body {
      margin-top: 11px;
      position: relative;
      height: 100%;
      padding-right: 10px;

      &__wrapper {
        margin: 0 20px 0 30px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        position: relative;
        height: 100%;
      }

      &__message {
        height: 100%;
        cursor: text;
        position: relative;
      }
    }

    &-footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin: 15px 30px;
      transition: .3s;
      min-height: 55px;
      position: relative;

      &:after {
        @include line-after-top;
        left: 0;
        right: 0;
        width: 100%;
        top: -15px
      }

      &-send {
        position: relative;
        margin-left: 14px;

        &__text {
          position: absolute;
        }
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/Templates/TemplateOpen/TemplateOpen.vue